import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Modal from "../../../../common/components/Modal/Modal.jsx";
import { ButtonBase, ButtonIconTrash, InputAutoresize, InputSelect } from "../../../../common/components/form";
import InputBase from "../../../../common/components/form/InputBase.jsx";
import * as API from "../../../lib/api.js";
import Wait from "../../Wait/Wait.jsx";

const KEYWORDS_MIN = 2;
const KEYWORDS_MAX = 4;

const HEADLINES_MIN = 3;
const HEADLINES_MAX = 15;

const DESCRIPTION_MIN = 2;
const DESCRIPTION_MAX = 4;

const SITELINKS_MIN = 1;
const SITELINKS_MAX = 6;

/**
 *
 * @param {*} props
 * id
 * show
 * data
 * GTC
 * onModalClose
 * @returns
 */
export default function WebsiteMarketingModal(props) {
    const { t } = useTranslation();
    const { id, show, data: predata, GTC, onModalClose } = props;

    const [wait, setWait] = React.useState(false);
    const [fieldErrors, setFieldErrors] = React.useState({});

    const [dataModified, setDataModified] = React.useState(Date.now());
    const [data, setData] = React.useState({ location: 1, period: "month", ...predata });
    const _dataKeys = Object.keys(data);
    const _dataKeywords = _dataKeys.filter(key => /^keywords\-\d+$/.test(key)).length;
    const _dataHeadlines = _dataKeys.filter(key => /^headlines\-\d+$/.test(key)).length;
    const _dataDescription = _dataKeys.filter(key => /^descriptions\-\d+$/.test(key)).length;
    const _dataSitelinks = _dataKeys.filter(key => /^sitelinks\-\d+$/.test(key)).length;

    React.useEffect(() => {
        if (!show) {
            return;
        }

        const generate = !data?.campaignName && _dataKeywords === 0 && _dataHeadlines === 0 && _dataDescription === 0;
        generate && _aiGenerate();
    }, [show, data]);

    const [keywords, setKeywords] = React.useState(
        new Array(Math.max(_dataKeywords, KEYWORDS_MIN)).fill(null).map(_ => ({}))
    );
    const [headlines, setHeadlines] = React.useState(
        new Array(Math.max(_dataHeadlines, HEADLINES_MIN)).fill(null).map(_ => ({}))
    );
    const [descriptions, setDescriptions] = React.useState(
        new Array(Math.max(_dataDescription, DESCRIPTION_MIN)).fill(null).map(_ => ({}))
    );
    const [sitelinks, setSitelinks] = React.useState(
        new Array(Math.max(_dataSitelinks, SITELINKS_MIN)).fill(null).map(_ => ({}))
    );

    async function _aiGenerate() {
        setWait("<h2>Generating</h2>");
        const response = await API.ProjectAiGoogleAds(id);

        if (response?.fields?.GoogleAds) {
            const _data = { ...data, ...response.fields.GoogleAds };
            _setData(_data);

            // ...
            const _dataKeys = Object.keys(_data);
            const _dataKeywords = _dataKeys.filter(key => /^keywords\-\d+$/.test(key)).length;
            const _dataHeadlines = _dataKeys.filter(key => /^headlines\-\d+$/.test(key)).length;
            const _dataDescription = _dataKeys.filter(key => /^descriptions\-\d+$/.test(key)).length;
            const _dataSitelinks = _dataKeys.filter(key => /^sitelinks\-\d+$/.test(key)).length;
            setKeywords(new Array(Math.max(_dataKeywords, KEYWORDS_MIN)).fill(null).map(_ => ({})));
            setHeadlines(new Array(Math.max(_dataHeadlines, HEADLINES_MIN)).fill(null).map(_ => ({})));
            setDescriptions(new Array(Math.max(_dataDescription, DESCRIPTION_MIN)).fill(null).map(_ => ({})));
            setSitelinks(new Array(Math.max(_dataSitelinks, SITELINKS_MIN)).fill(null).map(_ => ({})));
        }

        setWait(false);
    }

    function _setData(data) {
        setData(data);
        setDataModified(Date.now());
    }

    function onLocationSelect(value) {
        _setData({ ...data, location: parseInt(value) });
    }

    function onPeriodSelect(value) {
        _setData({ ...data, period: value });
    }

    function onChange(event, index) {
        console.clear();
        const { name, value } = event.target;

        const newData = { ...data };
        const newFieldErrors = { ...fieldErrors };

        console.warn(newData);

        const namePath = name.split(".");
        {
            const lastKey = namePath[namePath.length - 1];
            let object = newData;
            for (let i = 0, key; i < namePath.length - 1; i++) {
                key = namePath[i];
                object = object[key] ?? {};
            }
            object[lastKey] = value;
            console.log("=", lastKey, object, object[lastKey]);
        }

        if (!value.trim()) {
            newFieldErrors[name] = t("ErrorFieldEmpty");
        } else if (name === `headlines-${index}` && value.length > 30) {
            newFieldErrors[name] = t("MarketingTooLongHeadline");
        } else if (name === `descriptions-${index}` && value.length > 90) {
            newFieldErrors[name] = t("MarketingTooLongDescription");
        } else {
            delete newFieldErrors[name];
        }

        _setData(newData);
        setFieldErrors(newFieldErrors);
    }

    function onAddItemClick(name) {
        switch (name) {
            case "keywords":
                const newKeywordItem = [...keywords, {}];
                setKeywords(newKeywordItem);
                break;

            case "headlines":
                const newHeadlineItem = [...headlines, {}];
                setHeadlines(newHeadlineItem);
                break;

            case "descriptions":
                const newDescriptionItem = [...descriptions, {}];
                setDescriptions(newDescriptionItem);
                break;

            case "sitelinks":
                const newSitelinkItem = [...sitelinks, {}];
                setSitelinks(newSitelinkItem);
                break;
        }
    }

    function onItemRemove(item, name) {
        switch (name) {
            case "keywords":
                const newKeywordItem = keywords.filter(a => a !== item);
                setKeywords(newKeywordItem);
                break;

            case "headlines":
                const newHeadlineItem = headlines.filter(a => a !== item);
                setHeadlines(newHeadlineItem);
                break;

            case "descriptions":
                const newDescriptionItem = descriptions.filter(a => a !== item);
                setDescriptions(newDescriptionItem);
                break;

            case "sitelinks":
                const newSitelinkItem = sitelinks.filter(a => a !== item);
                setSitelinks(newSitelinkItem);
                break;
        }
    }

    async function onCampaignCreate() {
        setWait(true);

        await API.GoogleAdsAdd(id, data).then(res => {
            if (!res.ok && res.error) {
                return toast.error(res.error);
            }

            toast.success(t("MarketingSuccess"));
        });

        setWait(false);

        _setData({
            location: 1,
            period: "month",
        });

        onModalClose({ load: true });
    }

    const allowSave = !!(
        !Object.values(fieldErrors).length &&
        data?.campaignName &&
        data?.location &&
        (() => {
            let countByTypes = {};
            let emptyCount = 0;

            for (const key in data) {
                const type = key.match(/^(\w+)-\d+$/)?.[1];

                if (!type) {
                    continue;
                }

                countByTypes[type] = (countByTypes[type] || 0) + 1;

                emptyCount +=
                    typeof data[key] === "string"
                        ? data[key]?.trim().length === 0
                            ? 1
                            : 0
                        : (() => {
                              for (const k in data[key]) {
                                  if (data[key][k]?.trim().length === 0) {
                                      return 1;
                                  }
                              }

                              return 0;
                          })();
            }

            if (emptyCount > 0) {
                return false;
            }

            return (
                countByTypes["keywords"] !== undefined &&
                countByTypes["keywords"] >= KEYWORDS_MIN &&
                countByTypes["headlines"] !== undefined &&
                countByTypes["headlines"] >= HEADLINES_MIN &&
                countByTypes["descriptions"] !== undefined &&
                countByTypes["descriptions"] >= DESCRIPTION_MIN &&
                countByTypes["sitelinks"] !== undefined &&
                countByTypes["sitelinks"] >= SITELINKS_MIN
            );
        })()
    );

    return (
        <Modal show={show}>
            <form action="#" onSubmit={e => e.preventDefault()}>
                <div className="website-marketing-modal-category">
                    <h3>{t("WebsiteMarketingCampaignName")}</h3>

                    <InputBase
                        name="campaignName"
                        onChange={e => onChange(e)}
                        value={data?.campaignName || ""}
                        placeholder={t("WebsiteMarketingCampaignName")}
                        error={fieldErrors?.campaignName}
                        disabled={wait}
                    />
                </div>

                <div className="website-marketing-modal">
                    <div className="website-marketing-modal-category">
                        <h3>{t("WebsiteMarketingGeo")}</h3>

                        <InputSelect
                            key={dataModified}
                            fullWidth
                            onSelect={onLocationSelect}
                            options={[
                                { value: 1, text: "World" },
                                ...(GTC || []).map(({ id: value, name, country_code }) => ({
                                    value,
                                    text: `(${country_code}) ${name}`,
                                })),
                            ]}
                            selected={data?.location || ""}
                            filterField
                            disabled={wait}
                        />
                    </div>

                    <div className="website-marketing-modal-category">
                        <h3>{t("WebsiteMarketingPeriod")}</h3>

                        <InputSelect
                            key={dataModified}
                            fullWidth
                            onSelect={onPeriodSelect}
                            options={[
                                { value: "month", text: "Month" },
                                { value: "week", text: "Week" },
                            ]}
                            selected={data?.period || ""}
                            disabled={wait}
                        />
                    </div>

                    <div className="website-marketing-modal-category">
                        <h3>{t("WebsiteMarketingKeywords")}</h3>
                        <p>{t("WebsiteMarketingKeywordsText")}</p>

                        {keywords.map((item, index) => (
                            <div key={index} className="website-marketing-modal-category-item">
                                <div
                                    style={{
                                        display: "grid",
                                        gridAutoFlow: "row",
                                        paddingRight: "10px",
                                    }}
                                >
                                    <InputBase
                                        name={`keywords-${index}`}
                                        onChange={e => onChange(e, index)}
                                        value={data?.[`keywords-${index}`] || ""}
                                        placeholder="Keyword"
                                        error={fieldErrors[`keywords-${index}`]}
                                        disabled={wait}
                                    />
                                </div>

                                <div>
                                    <ButtonIconTrash
                                        noMargin
                                        disabled={keywords.length <= KEYWORDS_MIN || wait}
                                        onClick={() => onItemRemove(item, "keywords")}
                                    />
                                </div>
                            </div>
                        ))}

                        <div>
                            <ButtonBase
                                dashed
                                onClick={() => onAddItemClick("keywords")}
                                disabled={(KEYWORDS_MAX && keywords.length >= KEYWORDS_MAX) || wait}
                            >
                                {t("PlusAdd")}
                            </ButtonBase>
                        </div>
                    </div>

                    <div className="website-marketing-modal-category">
                        <h3>{t("WebsiteMarketingHeadlines")}</h3>
                        <p>{t("WebsiteMarketingHeadlinesText")}</p>

                        {headlines.map((item, index) => (
                            <div key={index} className="website-marketing-modal-category-item">
                                <div
                                    style={{
                                        display: "grid",
                                        gridAutoFlow: "row",
                                        paddingRight: "10px",
                                    }}
                                >
                                    <InputBase
                                        name={`headlines-${index}`}
                                        onChange={e => onChange(e, index)}
                                        value={data?.[`headlines-${index}`] || ""}
                                        placeholder="Headline"
                                        error={fieldErrors[`headlines-${index}`]}
                                        disabled={wait}
                                    />
                                </div>

                                <div>
                                    <ButtonIconTrash
                                        noMargin
                                        disabled={headlines.length <= HEADLINES_MIN || wait}
                                        onClick={() => onItemRemove(item, "headlines")}
                                    />
                                </div>
                            </div>
                        ))}

                        <div>
                            <ButtonBase
                                dashed
                                onClick={() => onAddItemClick("headlines")}
                                disabled={(HEADLINES_MAX && headlines.length >= HEADLINES_MAX) || wait}
                            >
                                {t("PlusAdd")}
                            </ButtonBase>
                        </div>
                    </div>

                    <div className="website-marketing-modal-category">
                        <h3>{t("WebsiteMarketingDescriptions")}</h3>
                        <p>{t("WebsiteMarketingDescriptionsText")}</p>

                        {descriptions.map((item, index) => (
                            <div key={index} className="website-marketing-modal-category-item">
                                <div
                                    style={{
                                        display: "grid",
                                        gridAutoFlow: "row",
                                        paddingRight: "10px",
                                    }}
                                >
                                    <InputAutoresize
                                        name={`descriptions-${index}`}
                                        onChange={e => onChange(e, index)}
                                        value={data?.[`descriptions-${index}`] || ""}
                                        placeholder="Descriptions"
                                        error={fieldErrors[`descriptions-${index}`]}
                                        disabled={wait}
                                    />
                                </div>

                                <div>
                                    <ButtonIconTrash
                                        noMargin
                                        disabled={descriptions.length <= DESCRIPTION_MIN || wait}
                                        onClick={() => onItemRemove(item, "descriptions")}
                                    />
                                </div>
                            </div>
                        ))}

                        <div>
                            <ButtonBase
                                dashed
                                onClick={() => onAddItemClick("descriptions")}
                                disabled={(DESCRIPTION_MAX && descriptions.length >= DESCRIPTION_MAX) || wait}
                            >
                                {t("PlusAdd")}
                            </ButtonBase>
                        </div>
                    </div>

                    {/* Sitelinks */}
                    <div className="website-marketing-modal-category">
                        <h3>{t("WebsiteMarketingSitelinks")}</h3>
                        <p>{t("WebsiteMarketingSitelinksText")}</p>

                        {sitelinks.map((item, index) => (
                            <div key={index} className="website-marketing-modal-category-item">
                                <div
                                    style={{
                                        display: "grid",
                                        gridAutoFlow: "row",
                                        gridGap: "10px",
                                        paddingRight: "10px",
                                    }}
                                >
                                    <InputAutoresize
                                        name={`sitelinks-${index}.link_text`}
                                        onChange={e => onChange(e, index)}
                                        value={data?.[`sitelinks-${index}`]?.link_text || ""}
                                        placeholder="Link text"
                                        error={fieldErrors[`sitelinks-${index}`]}
                                        disabled={wait}
                                    />

                                    <InputAutoresize
                                        name={`sitelinks-${index}.description1`}
                                        onChange={e => onChange(e, index)}
                                        value={data?.[`sitelinks-${index}`]?.description1 || ""}
                                        placeholder="Description 1"
                                        error={fieldErrors[`sitelinks-${index}`]}
                                        disabled={wait}
                                    />

                                    <InputAutoresize
                                        name={`sitelinks-${index}.description2`}
                                        onChange={e => onChange(e, index)}
                                        value={data?.[`sitelinks-${index}`]?.description2 || ""}
                                        placeholder="Descriptions 2"
                                        error={fieldErrors[`sitelinks-${index}`]}
                                        disabled={wait}
                                    />

                                    {/* <InputAutoresize
                                        name={`sitelinks-${index}.final_urls`}
                                        onChange={e => onChange(e, index)}
                                        value={data?.[`sitelinks-${index}`]?.final_url || ""}
                                        placeholder="Final url"
                                        error={fieldErrors[`sitelinks-${index}`]}
                                        disabled={wait}
                                    /> */}
                                </div>

                                <div>
                                    <ButtonIconTrash
                                        noMargin
                                        disabled={sitelinks.length <= SITELINKS_MIN || wait}
                                        onClick={() => onItemRemove(item, "sitelinks")}
                                    />
                                </div>
                            </div>
                        ))}

                        <div>
                            <ButtonBase
                                dashed
                                onClick={() => onAddItemClick("sitelinks")}
                                disabled={(SITELINKS_MAX && sitelinks.length >= SITELINKS_MAX) || wait}
                            >
                                {t("PlusAdd")}
                            </ButtonBase>
                        </div>
                    </div>
                </div>

                <div className="website-marketing-modal-category-actions">
                    <div style={{ display: "grid", gridAutoFlow: "column", gridGap: "20px" }}>
                        <ButtonBase onClick={() => onModalClose()} disabled={wait}>
                            {t("Cancel")}
                        </ButtonBase>

                        <ButtonBase onClick={() => _aiGenerate()} disabled={wait}>
                            {t("Generate")}
                        </ButtonBase>

                        <ButtonBase invert onClick={() => onCampaignCreate(data)} disabled={!allowSave || wait}>
                            {t("WebsiteMarketingCampaignCreate")}
                        </ButtonBase>
                    </div>
                </div>
            </form>

            <Wait show={wait} />
        </Modal>
    );
}
